import { graphQlClient, updateHeaders } from '@config/graphqlClient';
import ProcessesGraphQL from '@graphql/process.queries';
import UserGraphQL from '@graphql/user.queries';
import { findProcessAcrossOrganizationsRequest, ProcessWithOrganizationContext } from '@models/process.model';
import { getSelfWithOrganizationRequest } from '@models/user.model';
import { useAppSelector } from '@redux/hooks';
import { setOrganizationData, switchOrganization } from '@redux/reducers/organization.reducer';
import { setUserDetails, setUserOrganizations } from '@redux/reducers/user.reducer';
import { RootState } from '@redux/store';
import { useCallback } from 'react';
import { useDispatch } from 'react-redux';

interface UseCrossOrgProcessCheckReturn {
  checkProcessAcrossOrganizations: (processId: string) => Promise<ProcessWithOrganizationContext | null>;
  switchToProcessOrganization: (result: ProcessWithOrganizationContext) => Promise<void>;
}

export const useCrossOrgProcessCheck = (): UseCrossOrgProcessCheckReturn => {
  const dispatch = useDispatch();
  const { organizations } = useAppSelector((state: RootState) => state.user);
  const { organizationId: currentOrgId } = useAppSelector((state: RootState) => state.organization);

  const checkProcessAcrossOrganizations = useCallback(
    async (processId: string): Promise<ProcessWithOrganizationContext | null> => {
      // Skip if user only has one organization
      if (!organizations || organizations.length <= 1) {
        return null;
      }

      try {
        const data: findProcessAcrossOrganizationsRequest = await graphQlClient.request(
          ProcessesGraphQL.queries.findProcessAcrossOrganizations,
          { processId },
        );

        if (data?.findProcessAcrossOrganizations) {
          const result = data.findProcessAcrossOrganizations;
          // Only return if found in a different organization
          if (result.organizationId !== currentOrgId) {
            return result;
          }
        }

        return null;
      } catch (err) {
        console.error('Error checking process across organizations:', err);
        return null;
      }
    },
    [organizations, currentOrgId],
  );

  const switchToProcessOrganization = useCallback(
    async (result: ProcessWithOrganizationContext) => {
      try {
        // Step 1: Update Redux state to switch organization context
        dispatch(switchOrganization(result.organizationId, result.organizationName, undefined));

        // Step 2: Update GraphQL client headers to use new organization context
        updateHeaders({
          'X-Organization-Id': result.organizationId,
        });

        // Step 3: Fetch fresh user data with the new organization context to get correct role
        const userData: getSelfWithOrganizationRequest = await graphQlClient.request(
          UserGraphQL.queries.getSelfWithOrganization,
        );

        const user = userData.getSelfWithOrganization.user;
        const role = userData.getSelfWithOrganization.role;
        const organization = userData.getSelfWithOrganization.organization;
        const orgs = userData.getSelfWithOrganization.organizations || [];

        // Step 4: Update all Redux state with fresh data from the new organization context
        if (organization) {
          dispatch(setOrganizationData(organization));
        }
        dispatch(setUserDetails({ ...user, role }));
        dispatch(setUserOrganizations(orgs));
      } catch (err) {
        console.error('Error switching organization:', err);
        throw err;
      }
    },
    [dispatch],
  );

  return {
    checkProcessAcrossOrganizations,
    switchToProcessOrganization,
  };
};
